import { Asset, Report, Roles, SeizureRestraintRecord } from "types";
import "./AssetCss.css";
import AssetOwnerCard from "components/card/AssetOwnerCard";
import { useState } from "react";
import {
  updateAssetDecsion,
  updateAsset,
  fetchAssetReferralById,
} from "services/assetRecoveryService";
import { useTooltip } from "context/TooltipProvider";
import { formatDate, hasRole } from "utils/helper";
import { useAuth } from "context/AuthContext";
import FileRow from "components/file-components/file-row";
import { SeizureRestrainRecords } from "./ProvisionRecord";
import { BasePopup } from "components/pop-up/BasePopup";
import ReadMoreText from "components/ReadMoreText";
import { useTranslation } from "react-i18next";

interface AssetDetailProps {
  asset: Asset;
  action_status: string;
  onAssetCreated?: (data: any) => void;
  setActiveTab?: React.Dispatch<React.SetStateAction<string>>;
  referralId?: string;
  fileRowProps: any;
  reports?: Report[];
  recoveryReports?: Report[];
  // New props for parent actions
  onAddGovernance?: () => void;
  onEditGovernance?: () => void;
  onAddProvision?: () => void;
  records: SeizureRestraintRecord[];
  onEdit: (record: SeizureRestraintRecord) => void;
  onDelete: (recordId: string) => void;
  description: string;
  title: string;
  showDetail: boolean;
  setShowDetail: React.Dispatch<React.SetStateAction<boolean>>;
}

function StatusCell({
  value,
  map,
  fallback,
}: {
  value?: string;
  map: Record<string, { label: string; color: string }>;
  fallback: string;
}) {
  const choice = value && map[value];
  return (
    <span className={`font-bold ${choice ? choice.color : "text-gray-600"}`}>
      {choice ? choice.label : fallback}
    </span>
  );
}

export default function AssetDetail({
  asset,
  action_status,
  onAssetCreated,
  setActiveTab,
  referralId,
  fileRowProps,
  reports,
  recoveryReports,
  onAddGovernance,
  onEditGovernance,
  onAddProvision,
  onDelete,
  onEdit,
  records,
  description,
  title,
  showDetail,
  setShowDetail,
}: AssetDetailProps) {
  const assetConditions: Record<string, { label: string; color: string }> = {
    on_prohibition: { label: "On Prohibition", color: "text-red-600" },
    no_longer_under_prohibition: {
      label: "No longer Under Prohibition",
      color: "text-green-600",
    },
    not_prohibited: { label: "Not Prohibited", color: "text-green-600" },
  };

  const { loginData } = useAuth();
  const isAssetProsecutor = hasRole(loginData?.roles, [Roles.assetProsecutor]);

  const courtVerdictChoices: Record<string, { label: string; color: string }> =
    {
      guilty: { label: "Guilty", color: "text-red-600" },
      not_guilty: { label: "Not Guilty", color: "text-green-600" },
    };

  const { showTooltip } = useTooltip();
  const { t } = useTranslation();
  const [decision, setDecision] = useState("");
  const [prohibitionDecision, setProhibitionDecision] = useState("");
  const [loading, setLoading] = useState(false);
  const [prohibitionLoading, setProhibitionLoading] = useState(false);

  // Check if governance data exists
  const hasGovernanceData = Boolean(
    asset?.governing_body ||
    asset?.governing_body_contact ||
    asset?.governing_body_region ||
    asset?.governing_body_zone ||
    asset?.governing_body_woreda ||
    asset?.governing_body_house_number ||
    asset?.governing_body_town ||
    asset?.reason_for_governing ||
    asset?.authorizing_body ||
    asset?.governing_duration_start_date ||
    asset?.governing_duration_end_date,
  );

  const handleProsecutorDecision = async (
    id: string,
    prosecutor_decision_type: string,
  ) => {
    if (!prosecutor_decision_type) {
      showTooltip(t("decision_type_is_required"), "error");
      return;
    }
    setLoading(true);
    try {
      const response = await updateAssetDecsion(id, prosecutor_decision_type);
      showTooltip(t("decision_submitted_successfully"), "success");
      setLoading(false);
      if (onAssetCreated && response) {
        onAssetCreated(response);
      }
      setActiveTab?.("Assets");
    } catch (error) {
      console.error(error);
      setLoading(false);
      showTooltip(t("failed_to_submit_decision"), "error");
    }
  };

  const handleProhibitionDecision = async (id: string, decision: string) => {
    if (!decision) {
      showTooltip(t("prohibition_decision_is_required"), "error");
      return;
    }

    setProhibitionLoading(true);
    try {
      const updatedAsset = await updateAsset(id, {
        prohabition_court_verdict: decision,
        is_asset_prohibited: decision === "Prohabited" ? true : false,
      });
      if (updatedAsset) {
        showTooltip(
          t("prohibition_decision_submitted_successfully"),
          "success",
        );
        setProhibitionLoading(false);
        const response = await fetchAssetReferralById(referralId as string);
        if (onAssetCreated && response) {
          onAssetCreated(response);
          setActiveTab?.("Assets");
        }
      }
    } catch (error) {
      console.error(error);
      setProhibitionLoading(false);
      showTooltip(t("failed_to_submit_prohibition_decision"), "error");
    }
  };

  return (
    <BasePopup
      isOpen={showDetail}
      position="center"
      title={title}
      onClose={() => setShowDetail(false)}
      description={description}
      w="900px"
    >
      {" "}
      <div className="space-y-6">
        {/* Asset Information Section */}
        {asset.prosecutor_decision_type != null && (
          <div className="mt-3 p-4 border rounded-xl border-red-900">
            <h3 className="asset-detail-title mb-2">Prosecutor Decision</h3>
            <p className="text-sm text-gray-800 font-semibold">
              {asset.prosecutor_decision_type?.toLocaleUpperCase() ||
                "Not provided"}
            </p>
          </div>
        )}

        {asset.prohabition_court_verdict != null && (
          <div className="mt-1 p-4 border rounded-xl border-red-900">
            <h3 className="asset-detail-title mb-2">
              Prohibition Court Verdict
            </h3>
            <p className="text-sm text-gray-800 font-semibold">
              {asset.prohabition_court_verdict?.toLocaleUpperCase() ||
                "Not provided"}
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 my-3">
          {isAssetProsecutor && asset?.prohabition_court_verdict && (
            <>
              {!hasGovernanceData && (
                <button
                  onClick={onAddGovernance}
                  className="bg-[#212121] px-3 py-2 text-white font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-[#414040]"
                >
                  Add Governance
                </button>
              )}
              {asset?.prohabition_court_verdict === "Prohabited" && (
                <button
                  onClick={onAddProvision}
                  className="bg-[#212121] px-3 py-2 text-white font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-[#414040]"
                >
                  Add Provision
                </button>
              )}
            </>
          )}
        </div>

        {/* Prohibition Decision Form */}
        {isAssetProsecutor && !asset?.prohabition_court_verdict && (
          <div className="mt-6 p-5 bg-gray-50 border border-gray-200 rounded-xl shadow-sm">
            <h3 className="text-md font-semibold text-gray-900 mb-2">
              Prohibition Decision
            </h3>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleProhibitionDecision(
                  asset.id as string,
                  prohibitionDecision,
                );
              }}
              className="space-y-4"
            >
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700">
                  Select Prohibition Decision:
                </label>
                <select
                  value={prohibitionDecision}
                  onChange={(e) => setProhibitionDecision(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select decision</option>
                  <option value="Prohabited">Prohibited</option>
                  <option value="Not Prohabited">Not Prohibited</option>
                </select>
              </div>

              <button
                type="submit"
                disabled={!prohibitionDecision || prohibitionLoading}
                className={`px-5 py-2 rounded-lg text-white font-medium transition-all ${
                  !prohibitionDecision
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-[#212121] px-3 py-2 text-white font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-[#414040]"
                }`}
              >
                {prohibitionLoading ? "Submitting..." : "Submit Decision"}
              </button>
            </form>
          </div>
        )}

        {/* Conditions Table */}
        <div className="mb-6">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>
                <th className="pr-4 py-2 text-left asset-detail-label w-[30%]">
                  Asset Condition
                </th>
                <th className="px-4 py-2 text-left asset-detail-label w-[35%]">
                  Case Condition
                </th>
                <th className="px-4 py-2 text-left asset-detail-label">
                  Court Verdict Condition
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="pr-4 py-2">
                  <StatusCell
                    value={asset.asset_condition}
                    map={assetConditions}
                    fallback="On Prohibition"
                  />
                </td>
                <td className="px-4 py-2 text-orange-600 font-bold">
                  {asset.case_condition || "Crime on Investigation"}
                </td>
                <td className="px-4 py-2">
                  <StatusCell
                    value={asset.court_verdict_decision_type}
                    map={courtVerdictChoices}
                    fallback="Guilty"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Asset Details */}
        <div className="space-y-3 mb-6">
          <div className="flex border-b border-gray-200 pb-2 justify-between">
            <div className="w-2/3 font-medium asset-detail-label">
              Asset Location
            </div>
            <div className="w-1/3 asset-detail-value">
              {[
                asset.asset_location_region,
                asset.asset_location_zone,
                asset.asset_location_woreda,
                asset.asset_location_town,
              ]
                .filter(Boolean)
                .join(" / ") || "-"}
            </div>
          </div>

          <div className="flex border-b border-gray-200 pb-2 justify-between">
            <div className="w-2/3 font-medium asset-detail-label">
              Asset Category
            </div>
            <div className="w-1/3 asset-detail-value">
              {asset.asset_category || "-"}
            </div>
          </div>

          <div className="flex border-b border-gray-200 pb-2 justify-between">
            <div className="w-2/3 font-medium asset-detail-label">
              Asset Number
            </div>
            <div className="w-1/3 asset-detail-value">
              {asset.asset_number || "-"}
            </div>
          </div>

          <div className="flex border-b border-gray-200 pb-2 justify-between">
            <div className="w-2/3 font-medium asset-detail-label">
              Estimation of Money
            </div>
            <div className="w-1/3 asset-detail-value">
              {asset.estimation_of_money
                ? `${Number(asset.estimation_of_money).toLocaleString()} ETB`
                : "-"}
            </div>
          </div>

          <div className="flex border-b border-gray-200 pb-2 justify-between">
            <div className="w-2/3 font-medium asset-detail-label">Quantity</div>
            <div className="w-1/3 asset-detail-value">
              {asset.quantity || "-"}
            </div>
          </div>

          <div className="flex border-b border-gray-200 pb-2 justify-between">
            <div className="w-2/3 font-medium asset-detail-label">
              Asset Owned Date
            </div>
            <div className="w-1/3 asset-detail-value">
              {asset.asset_owned_date
                ? formatDate(asset.asset_owned_date)
                : "-"}
            </div>
          </div>

          <div className="flex border-b border-gray-200 pb-2 justify-between">
            <div className="w-2/3 font-medium asset-detail-label">
              Crime Category
            </div>
            <div className="w-1/3 asset-detail-value">
              {asset.crime_category || "-"}
            </div>
          </div>

          <div className="flex border-b border-gray-200 pb-2 justify-between">
            <div className="w-2/3 font-medium asset-detail-label">
              Crime Location
            </div>
            <div className="w-1/3 asset-detail-value">
              {[
                asset.crime_location_region,
                asset.crime_location_zone,
                asset.crime_location_woreda,
                asset.crime_location_town,
              ]
                .filter(Boolean)
                .join(" / ") || "-"}
            </div>
          </div>

          <div className="flex border-b border-gray-200 pb-2 justify-between">
            <div className="w-2/3 font-medium asset-detail-label">
              Owner Relationship with Suspect
            </div>
            <div className="w-1/3 asset-detail-value">
              {asset.owner_relationship_with_suspect || "-"}
            </div>
          </div>

          <div className="flex border-b border-gray-200 pb-2 justify-between">
            <div className="w-2/3 font-medium asset-detail-label">
              Is Owner Above 18?
            </div>
            <div className="w-1/3 asset-detail-value">
              {asset.is_owner_above_18 ? "Yes" : "No"}
            </div>
          </div>

          <div className="flex border-b border-gray-200 pb-2 justify-between">
            <div className="w-2/3 font-medium asset-detail-label">
              Is Suspect the Owner?
            </div>
            <div className="w-1/3 asset-detail-value">
              {asset.is_the_suspect_the_owner ? "Yes" : "No"}
            </div>
          </div>
        </div>

        {/* Asset Requiring Motion Section */}
        {asset.how_asset_acquired !== null &&
          asset.how_asset_acquired !== "" && (
            <div>
              <h3 className="asset-detail-title mb-3">Asset Requiring</h3>
              <div className="p-4">
                <p className="text-sm text-gray-700 leading-relaxed border-b border-gray-200 pb-2">
                  {asset.how_asset_acquired || "No narration provided"}
                </p>
              </div>
            </div>
          )}

        {/* Reason for Not Prohibition */}
        {asset.asset_condition === "not_prohibited" &&
          asset.reason_for_not_prohibition && (
            <div>
              <h3 className="asset-detail-title mb-3">
                Reason for Not Prohibition
              </h3>
              <ReadMoreText
                text={asset.reason_for_not_prohibition}
                maxLines={3}
              />
            </div>
          )}

        {/* Prohibition Court Details */}
        {asset.prohabition_court_verdict &&
          (asset.court_nmae ||
            asset.court_registration_number ||
            asset.date_of_prohabition) && (
            <div>
              <h3 className="asset-detail-title mb-3">
                Prohibition Court Details
              </h3>
              <div className="space-y-3">
                {asset.court_nmae && (
                  <div className="flex border-b border-gray-200 pb-2 justify-between">
                    <div className="w-2/3 font-medium asset-detail-label">
                      Court Name
                    </div>
                    <div className="w-1/3 asset-detail-value">
                      {asset.court_nmae}
                    </div>
                  </div>
                )}
                {asset.court_registration_number && (
                  <div className="flex border-b border-gray-200 pb-2 justify-between">
                    <div className="w-2/3 font-medium asset-detail-label">
                      Registration Number
                    </div>
                    <div className="w-1/3 asset-detail-value">
                      {asset.court_registration_number}
                    </div>
                  </div>
                )}
                {asset.date_of_prohabition && (
                  <div className="flex border-b border-gray-200 pb-2 justify-between">
                    <div className="w-2/3 font-medium asset-detail-label">
                      Date of Prohibition
                    </div>
                    <div className="w-1/3 asset-detail-value">
                      {formatDate(asset.date_of_prohabition)}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

        {/* Prohibition Removal Details */}
        {asset.prohabition_removal_reason && (
          <div>
            <h3 className="asset-detail-title mb-3">
              Prohibition Removal Details
            </h3>
            <div className="space-y-3">
              {asset.Court_name_for_removal && (
                <div className="flex border-b border-gray-200 pb-2 justify-between">
                  <div className="w-2/3 font-medium asset-detail-label">
                    Removal Court Name
                  </div>
                  <div className="w-1/3 asset-detail-value">
                    {asset.Court_name_for_removal}
                  </div>
                </div>
              )}
              {asset.court_level_for_removal && (
                <div className="flex border-b border-gray-200 pb-2 justify-between">
                  <div className="w-2/3 font-medium asset-detail-label">
                    Court Level
                  </div>
                  <div className="w-1/3 asset-detail-value">
                    {asset.court_level_for_removal}
                  </div>
                </div>
              )}
              {asset.court_region_for_removal && (
                <div className="flex border-b border-gray-200 pb-2 justify-between">
                  <div className="w-2/3 font-medium asset-detail-label">
                    Court Region
                  </div>
                  <div className="w-1/3 asset-detail-value">
                    {asset.court_region_for_removal}
                  </div>
                </div>
              )}
              {asset.court_zone_for_removal && (
                <div className="flex border-b border-gray-200 pb-2 justify-between">
                  <div className="w-2/3 font-medium asset-detail-label">
                    Court Zone
                  </div>
                  <div className="w-1/3 asset-detail-value">
                    {asset.court_zone_for_removal}
                  </div>
                </div>
              )}
              {asset.court_woreda_for_removal && (
                <div className="flex border-b border-gray-200 pb-2 justify-between">
                  <div className="w-2/3 font-medium asset-detail-label">
                    Court Woreda
                  </div>
                  <div className="w-1/3 asset-detail-value">
                    {asset.court_woreda_for_removal}
                  </div>
                </div>
              )}
              {asset.date_of_prohabition_removal && (
                <div className="flex border-b border-gray-200 pb-2 justify-between">
                  <div className="w-2/3 font-medium asset-detail-label">
                    Removal Date
                  </div>
                  <div className="w-1/3 asset-detail-value">
                    {formatDate(asset.date_of_prohabition_removal)}
                  </div>
                </div>
              )}
              <div className="mt-2">
                <p className="font-medium asset-detail-title mb-2">
                  Removal Reason
                </p>

                <ReadMoreText
                  maxLines={3}
                  text={asset.prohabition_removal_reason}
                  color="text-gray-700"
                />
              </div>
            </div>
          </div>
        )}

        {/* Asset Recovering Motion Section */}
        {asset.has_asset_recovery_decision && (
          <div>
            <h3 className="asset-detail-title mb-3">Asset Recovering</h3>
            <div className="p-4">
              <p className="text-sm text-gray-700 leading-relaxed border-b border-gray-200 pb-2">
                {asset.has_asset_recovery_decision
                  ? "This asset is under recovery decision."
                  : "-."}
              </p>
            </div>
          </div>
        )}

        {/* Asset Recovery Decision */}
        {asset.has_asset_recovery_decision && (
          <div>
            <h3 className="asset-detail-title mb-3">Asset Recovery Decision</h3>
            <div className="space-y-3">
              {asset.asset_recovery_court_court_decsion && (
                <div className="flex border-b border-gray-200 pb-2 justify-between">
                  <div className="w-2/3 font-medium asset-detail-label">
                    Court Decision
                  </div>
                  <div className="w-1/3 asset-detail-value">
                    {asset.asset_recovery_court_court_decsion}
                  </div>
                </div>
              )}
              {asset.aseet_recovery_court_decson_sumamry && (
                <div className="mt-2">
                  <p className="font-medium asset-detail-title mb-2">
                    Decision Summary
                  </p>

                  <ReadMoreText
                    text={asset.aseet_recovery_court_decson_sumamry}
                    maxLines={3}
                    color="text-gray-700"
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Appeal Status Indicators */}
        {(asset.is_prohabition_request_decision_appealed ||
          asset.is_asset_recovery_decision_appealed) && (
          <div className="grid grid-cols-2 gap-4 mt-4">
            {asset.is_prohabition_request_decision_appealed && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm font-semibold text-yellow-800">
                  Prohibition Decision Appealed
                </p>
              </div>
            )}
            {asset.is_asset_recovery_decision_appealed && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm font-semibold text-yellow-800">
                  Recovery Decision Appealed
                </p>
              </div>
            )}
          </div>
        )}

        {/* Asset Owners */}
        {Array.isArray(asset?.asset_owner_details) &&
          asset.asset_owner_details.length > 0 && (
            <div>
              <h3 className="asset-detail-title mb-3">Asset Owners</h3>
              <div className="p-2 flex flex-wrap">
                {asset.asset_owner_details.map((owner) => (
                  <AssetOwnerCard
                    person={owner?.person}
                    name={owner?.person?.full_name}
                    age={` ${owner?.person?.subcity ?? ""} / ${
                      owner?.person?.region ?? ""
                    } / ${owner?.person?.phone ?? ""}`}
                    key={owner?.id}
                  />
                ))}
              </div>
            </div>
          )}

        {/* Seizure Restrain Records */}
        {records.length > 0 && (
          <SeizureRestrainRecords
            records={records}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        )}

        {/* Execution Body Info */}
        {asset.execution_body && (
          <div>
            <h3 className="asset-detail-title mb-3">Execution Body</h3>
            <div className="space-y-3">
              <div className="flex border-b border-gray-200 pb-2 justify-between">
                <div className="w-2/3 font-medium asset-detail-label">
                  Execution Body
                </div>
                <div className="w-1/3 asset-detail-value">
                  {asset.execution_body}
                </div>
              </div>
              {asset.execution_body_contact && (
                <div className="flex border-b border-gray-200 pb-2 justify-between">
                  <div className="w-2/3 font-medium asset-detail-label">
                    Contact
                  </div>
                  <div className="w-1/3 asset-detail-value">
                    {asset.execution_body_contact}
                  </div>
                </div>
              )}
              {[
                asset.execution_body_region,
                asset.execution_body_zone,
                asset.execution_body_woreda,
                asset.execution_body_town,
              ].some(Boolean) && (
                <div className="flex border-b border-gray-200 pb-2 justify-between">
                  <div className="w-2/3 font-medium asset-detail-label">
                    Location
                  </div>
                  <div className="w-1/3 asset-detail-value">
                    {[
                      asset.execution_body_region,
                      asset.execution_body_zone,
                      asset.execution_body_woreda,
                      asset.execution_body_town,
                    ]
                      .filter(Boolean)
                      .join(" / ")}
                  </div>
                </div>
              )}
              {asset.execution_body_house_number && (
                <div className="flex border-b border-gray-200 pb-2 justify-between">
                  <div className="w-2/3 font-medium asset-detail-label">
                    House Number
                  </div>
                  <div className="w-1/3 asset-detail-value">
                    {asset.execution_body_house_number}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Governing Info Section */}
        {hasGovernanceData && (
          <div>
            <h3 className="asset-detail-title mb-3">Governing Info</h3>
            <div className="flex items-center gap-3 p-3 w-full border-b-2 mb-2 mt-4 justify-between">
              <div className="flex items-center gap-3">
                <img
                  src="https://ui-avatars.com/api/?name=User&background=0D8ABC&color=fff"
                  alt="User"
                  className="w-9 h-9 rounded-full"
                />
                <div>
                  <p className="font-medium text-gray-800">
                    {asset?.governing_body_contact || "-"}
                  </p>
                  <p className="text-sm text-gray-500">
                    {asset?.governing_body}
                  </p>
                </div>
              </div>
              {isAssetProsecutor &&
                action_status === "Prohibition On litigation" && (
                  <button
                    type="button"
                    onClick={onEditGovernance}
                    className="flex items-center gap-2 px-4 py-2 text-black rounded-lg transition-colors"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                    Edit
                  </button>
                )}
            </div>
            <div className="grid grid-cols-4 gap-4 w-full my-4">
              <div className="flex flex-col">
                <p className="text-sm font-semibold mb-4 text-[#9D0303]">
                  Governing Start Date
                </p>
                <p className="text-sm text-gray-700 leading-relaxed">
                  {asset?.governing_duration_start_date
                    ? formatDate(asset?.governing_duration_start_date)
                    : "-"}
                </p>
              </div>
              <div className="flex flex-col">
                <p className="text-sm font-semibold mb-4 text-[#9D0303]">
                  Governing End Date
                </p>
                <p className="text-sm text-gray-700 leading-relaxed">
                  {asset?.governing_duration_end_date
                    ? formatDate(asset?.governing_duration_end_date)
                    : "-"}
                </p>
              </div>
              <div className="flex flex-col">
                <p className="text-sm font-semibold mb-4 text-[#9D0303]">
                  Location
                </p>
                <p className="text-sm text-gray-700 leading-relaxed">
                  {[
                    asset?.governing_body_region,
                    asset?.governing_body_zone,
                    asset?.governing_body_woreda,
                    asset?.governing_body_town,
                  ]
                    .filter(Boolean)
                    .join(" / ") || "-"}
                </p>
              </div>
              <div className="flex flex-col">
                <p className="text-sm font-semibold mb-4 text-[#9D0303]">
                  Authorizing Body
                </p>
                <p className="text-sm text-gray-700 leading-relaxed">
                  {asset?.authorizing_body || "-"}
                </p>
              </div>
            </div>

            <div className="my-2">
              <p className="font-medium asset-detail-title mb-3">
                Governing Reason
              </p>
              <p className="text-sm text-gray-700 leading-relaxed">
                {asset?.reason_for_governing}
              </p>
            </div>

            {asset?.agrement_documents &&
              asset?.agrement_documents?.length > 0 &&
              asset?.agrement_documents?.map((file: any, index) => (
                <FileRow
                  key={file.document_uuid || index}
                  file={file}
                  {...fileRowProps}
                />
              ))}
          </div>
        )}
      </div>
    </BasePopup>
  );
}
